import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Button,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  FormControlLabel,
  Collapse,
  Divider,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import axios from "axios";

const ANALYST_OPTIONS = [
  { value: "market", label: "Market Analyst" },
  { value: "social", label: "Social Media Analyst" },
  { value: "news", label: "News Analyst" },
  { value: "fundamentals", label: "Fundamentals Analyst" },
];

const GURU_OPTIONS = [
  { value: "aswath_damodaran", label: "Aswath Damodaran" },
  { value: "ben_graham", label: "Ben Graham" },
  { value: "bill_ackman", label: "Bill Ackman" },
  { value: "cathie_wood", label: "Cathie Wood" },
  { value: "charlie_munger", label: "Charlie Munger" },
  { value: "michael_burry", label: "Michael Burry" },
  { value: "mohnish_pabrai", label: "Mohnish Pabrai" },
  { value: "peter_lynch", label: "Peter Lynch" },
  { value: "phil_fisher", label: "Phil Fisher" },
  { value: "rakesh_jhunjhunwala", label: "Rakesh Jhunjhunwala" },
  { value: "stanley_druckenmiller", label: "Stanley Druckenmiller" },
  { value: "warren_buffett", label: "Warren Buffett" },
  { value: "technical_analyst", label: "Technical Analyst" },
  { value: "fundamentals_analyst", label: "Fundamentals Analyst" },
  { value: "growth_analyst", label: "Growth Analyst" },
  { value: "news_sentiment_analyst", label: "News Sentiment Analyst" },
  { value: "sentiment_analyst", label: "Sentiment Analyst" },
  { value: "valuation_analyst", label: "Valuation Analyst" },
];

const LLM_PROVIDERS = [
  { value: "openai", label: "OpenAI", url: "https://api.openai.com/v1" },
];

// Model names are NOT hardcoded here — they are fetched from the backend
// (/api/config/status), whose single source of truth is agents/model_config.py.

const AGENT_DEFINITIONS = [
  {
    key: "tradingagents",
    label: "Comprehensive Trading Agent",
    description: "Multi-agent debate system with analyst team, bull/bear researchers, trader, and risk management",
  },
  {
    key: "research_agent",
    label: "Simple Research Agent",
    description: "Deep web research using Tavily search with multi-agent supervisor",
  },
  {
    key: "stock_agent",
    label: "Simple Stock Agent",
    description: "Structured stock analysis with buy/sell cases using Google ADK",
  },
  {
    key: "ta_agent",
    label: "Simple Technical Analysis Agent",
    description: "Algorithmic signal generation using SMA20/SMA200/MACD indicators (no LLM)",
  },
  {
    key: "guru_agents",
    label: "Guru Investors Agent",
    description: "Multi-guru analysis channeling legendary investors like Buffett, Munger, Graham, Lynch, and more",
  },
];

function ConfigForm({ onSubmit, initialTicker }) {
  const [ticker, setTicker] = useState(initialTicker || "SPY");

  useEffect(() => {
    if (initialTicker) {
      setTicker(initialTicker);
    }
  }, [initialTicker]);
  const [analysisDate, setAnalysisDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [selectedAgents, setSelectedAgents] = useState(["tradingagents", "stock_agent"]);
  const [selectedAnalysts, setSelectedAnalysts] = useState([
    "market",
    "social",
    "news",
    "fundamentals",
  ]);
  const [selectedGurus, setSelectedGurus] = useState(
    GURU_OPTIONS.map((g) => g.value)
  );
  const [researchDepth, setResearchDepth] = useState(1);
  const [llmProvider, setLlmProvider] = useState("openai");
  const [shallowModel, setShallowModel] = useState("");
  const [deepModel, setDeepModel] = useState("");
  const [modelOptions, setModelOptions] = useState({ shallow: [], deep: [] });

  // Fetch model defaults/options from the backend (single source of truth).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get("/api/config/status");
        const cfg = data?.config || {};
        const shallow = cfg.shallow_models || (cfg.quick_think_llm ? [cfg.quick_think_llm] : []);
        const deep = cfg.deep_models || (cfg.deep_think_llm ? [cfg.deep_think_llm] : []);
        if (cancelled) return;
        setModelOptions({ shallow, deep });
        setShallowModel(cfg.quick_think_llm || shallow[0] || "");
        setDeepModel(cfg.deep_think_llm || deep[0] || "");
      } catch (error) {
        console.error("Failed to fetch model config:", error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const getModelOptions = (type) => modelOptions[type] || [];

  const toggleAgent = (agentKey) => {
    setSelectedAgents((prev) =>
      prev.includes(agentKey)
        ? prev.filter((a) => a !== agentKey)
        : [...prev, agentKey]
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (selectedAgents.length === 0) return;

    const provider = LLM_PROVIDERS.find((p) => p.value === llmProvider);

    const tickers = ticker
      .split(/[\s,]+/)
      .map((t) => t.trim().toUpperCase())
      .filter((t) => t.length > 0);

    if (tickers.length === 0) return;

    const baseConfig = {
      analysis_date: analysisDate,
      selected_agents: selectedAgents,
      analysts: selectedAnalysts,
      selected_gurus: selectedGurus,
      research_depth: researchDepth,
      llm_provider: llmProvider,
      backend_url: provider.url,
      shallow_thinker: shallowModel,
      deep_thinker: deepModel,
    };

    if (tickers.length === 1) {
      onSubmit({ ticker: tickers[0], ...baseConfig });
    } else {
      onSubmit({ tickers, ...baseConfig });
    }
  };

  const isAgentSelected = (key) => selectedAgents.includes(key);

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Grid container spacing={3}>
        {/* Global Configuration */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                Global Configuration
              </Typography>

              <TextField
                fullWidth
                label="Ticker Symbol(s)"
                value={ticker}
                onChange={(e) => setTicker(e.target.value.toUpperCase())}
                margin="normal"
                required
                helperText="Enter one or more tickers separated by spaces or commas (e.g. AAPL, MSFT GOOG)"
                placeholder="SPY, AAPL, MSFT"
              />

              <TextField
                fullWidth
                label="Analysis Date"
                type="date"
                value={analysisDate}
                onChange={(e) => setAnalysisDate(e.target.value)}
                margin="normal"
                InputLabelProps={{ shrink: true }}
                required
              />
            </CardContent>
          </Card>
        </Grid>

        {/* LLM Configuration */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                LLM Configuration
              </Typography>

              <FormControl fullWidth margin="normal">
                <InputLabel>LLM Provider</InputLabel>
                <Select
                  value={llmProvider}
                  onChange={(e) => {
                    setLlmProvider(e.target.value);
                    const models = getModelOptions("shallow");
                    if (models.length > 0) {
                      setShallowModel(models[0]);
                      setDeepModel(getModelOptions("deep")[0] || models[0]);
                    }
                  }}
                  label="LLM Provider"
                >
                  {LLM_PROVIDERS.map((provider) => (
                    <MenuItem key={provider.value} value={provider.value}>
                      {provider.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Grid container spacing={2} sx={{ mt: 0 }}>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Quick-Thinking Model</InputLabel>
                    <Select
                      value={shallowModel}
                      onChange={(e) => setShallowModel(e.target.value)}
                      label="Quick-Thinking Model"
                    >
                      {getModelOptions("shallow").map((model) => (
                        <MenuItem key={model} value={model}>
                          {model}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Deep-Thinking Model</InputLabel>
                    <Select
                      value={deepModel}
                      onChange={(e) => setDeepModel(e.target.value)}
                      label="Deep-Thinking Model"
                    >
                      {getModelOptions("deep").map((model) => (
                        <MenuItem key={model} value={model}>
                          {model}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Agent Selection */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                Agent Selection
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Select which agents to run. Multiple agents run in parallel.
              </Typography>

              {AGENT_DEFINITIONS.map((agent, idx) => (
                <Box key={agent.key}>
                  {idx > 0 && <Divider sx={{ my: 1 }} />}
                  <Box>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isAgentSelected(agent.key)}
                          onChange={() => toggleAgent(agent.key)}
                          color="primary"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', lineHeight: 1.3 }}>
                            {agent.label}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {agent.description}
                          </Typography>
                        </Box>
                      }
                      sx={{ alignItems: 'flex-start', my: 0.5, '& .MuiCheckbox-root': { pt: 0.5 } }}
                    />

                    {/* TradingAgents config section */}
                    <Collapse in={agent.key === "tradingagents" && isAgentSelected("tradingagents")}>
                      <Box sx={{ ml: 4, mt: 1, mb: 1, pl: 2, borderLeft: '2px solid', borderColor: 'primary.main' }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Research Depth</InputLabel>
                              <Select
                                value={researchDepth}
                                onChange={(e) => setResearchDepth(e.target.value)}
                                label="Research Depth"
                              >
                                <MenuItem value={1}>Shallow - 1 debate round</MenuItem>
                                <MenuItem value={3}>Medium - 3 debate rounds</MenuItem>
                                <MenuItem value={5}>Deep - 5 debate rounds</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Analyst Team</InputLabel>
                              <Select
                                multiple
                                value={selectedAnalysts}
                                onChange={(e) => setSelectedAnalysts(e.target.value)}
                                input={<OutlinedInput label="Analyst Team" />}
                                renderValue={(selected) => (
                                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                    {selected.map((value) => {
                                      const analyst = ANALYST_OPTIONS.find(
                                        (a) => a.value === value,
                                      );
                                      return (
                                        <Chip
                                          key={value}
                                          label={analyst?.label}
                                          size="small"
                                        />
                                      );
                                    })}
                                  </Box>
                                )}
                              >
                                {ANALYST_OPTIONS.map((analyst) => (
                                  <MenuItem key={analyst.value} value={analyst.value}>
                                    <Checkbox
                                      checked={selectedAnalysts.indexOf(analyst.value) > -1}
                                    />
                                    <ListItemText primary={analyst.label} />
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                        </Grid>
                      </Box>
                    </Collapse>

                    {/* Guru Agents config section */}
                    <Collapse in={agent.key === "guru_agents" && isAgentSelected("guru_agents")}>
                      <Box sx={{ ml: 4, mt: 1, mb: 1, pl: 2, borderLeft: '2px solid', borderColor: 'primary.main' }}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Guru Team</InputLabel>
                          <Select
                            multiple
                            value={selectedGurus}
                            onChange={(e) => setSelectedGurus(e.target.value)}
                            input={<OutlinedInput label="Guru Team" />}
                            renderValue={(selected) => (
                              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                {selected.map((value) => {
                                  const guru = GURU_OPTIONS.find(
                                    (g) => g.value === value,
                                  );
                                  return (
                                    <Chip
                                      key={value}
                                      label={guru?.label}
                                      size="small"
                                    />
                                  );
                                })}
                              </Box>
                            )}
                          >
                            {GURU_OPTIONS.map((guru) => (
                              <MenuItem key={guru.value} value={guru.value}>
                                <Checkbox
                                  checked={selectedGurus.indexOf(guru.value) > -1}
                                />
                                <ListItemText primary={guru.label} />
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    </Collapse>
                  </Box>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Box textAlign="center">
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={selectedAgents.length === 0}
              sx={{ px: 4, py: 2, fontSize: "1.1rem" }}
            >
              {selectedAgents.length === 0
                ? "Select at least one agent"
                : `Start Analysis (${selectedAgents.length} agent${selectedAgents.length > 1 ? "s" : ""})`}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}

export default ConfigForm;
